import React, { useState, useEffect } from 'react';
import { Eye, Clock, User, Envelope, Phone, MapPin } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/admin/AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import Pagination, { usePagination } from '../../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const { currentPage, setCurrentPage, paginated, totalPages, totalItems, rangeStart, rangeEnd } = usePagination(quotes, 20);

  const statusOptions = [
    { value: 'nouveau', label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
    { value: 'en_cours', label: 'En cours', color: 'bg-amber-100 text-amber-700' },
    { value: 'valide', label: 'Validé', color: 'bg-[#2A9D8F]/10 text-[#2A9D8F]' },
    { value: 'devis_envoye', label: 'Devis envoyé', color: 'bg-purple-100 text-purple-700' },
    { value: 'cloture', label: 'Clôturé', color: 'bg-gray-100 text-gray-500' }
  ];

  useEffect(() => { fetchQuotes(); }, [statusFilter]);

  const fetchQuotes = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await axios.get(`${API_URL}/api/quotes?${params}`);
      setQuotes(response.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const getStatusBadge = (status) => { const opt = statusOptions.find(o => o.value === status) || statusOptions[0]; return <span className={`px-2 py-1 text-xs font-bold uppercase ${opt.color}`}>{opt.label}</span>; };

  const openDetails = async (quote) => {
    try { const response = await axios.get(`${API_URL}/api/quotes/${quote.id}`); setSelectedQuote(response.data); setDialogOpen(true); }
    catch (error) { toast.error('Erreur'); }
  };

  const updateStatus = async (quoteId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/api/quotes/${quoteId}/status?status=${newStatus}`);
      toast.success('Statut mis à jour');
      fetchQuotes();
      if (selectedQuote?.quote?.id === quoteId) setSelectedQuote(p => ({ ...p, quote: { ...p.quote, status: newStatus } }));
    } catch (error) { toast.error('Erreur'); }
    finally { setUpdating(false); }
  };

  return (
    <AdminLayout title="Demandes de Devis">
      <div className="flex gap-4 mb-6">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white border border-gray-200 text-gray-900 px-4 py-3 min-w-[200px]" data-testid="filter-status"><option value="">Tous les statuts</option>{statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-gray-200 bg-gray-50"><th className="text-left p-4 text-gray-500 text-xs uppercase">Date</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Client</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Contact</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Pièces</th><th className="text-left p-4 text-gray-500 text-xs uppercase">Statut</th><th className="text-right p-4 text-gray-500 text-xs uppercase">Actions</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={6} className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin mx-auto"></div></td></tr>
              : paginated.length > 0 ? paginated.map((q, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="p-4 text-gray-500 text-sm"><div className="flex items-center gap-2"><Clock size={14} className="text-gray-400" />{formatDate(q.created_at)}</div></td>
                  <td className="p-4"><p className="text-gray-900 font-medium">{q.first_name} {q.last_name}</p>{q.city && <p className="text-gray-400 text-xs flex items-center gap-1"><MapPin size={12} />{q.city}</p>}</td>
                  <td className="p-4"><p className="text-gray-600 text-sm flex items-center gap-2"><Envelope size={14} className="text-gray-400" />{q.email}</p><p className="text-gray-400 text-xs flex items-center gap-2"><Phone size={12} className="text-gray-400" />{q.phone}</p></td>
                  <td className="p-4 text-gray-900 font-medium">{q.selected_parts?.length || 0}</td>
                  <td className="p-4">
                    <select
                      value={q.status || 'nouveau'}
                      onChange={(e) => updateStatus(q.id, e.target.value)}
                      disabled={updating}
                      className={`text-xs font-bold uppercase border px-2 py-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E63946]/30 ${(statusOptions.find(o => o.value === q.status) || statusOptions[0]).color}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`status-select-${i}`}
                    >
                      {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </td>
                  <td className="p-4 text-right"><button onClick={() => openDetails(q)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 inline-flex items-center gap-2" data-testid={`view-quote-${i}`}><Eye size={16} />Détails</button></td>
                </tr>
              )) : <tr><td colSpan={6} className="p-8 text-center"><User size={48} className="text-gray-300 mx-auto mb-4" /><p className="text-gray-400">Aucune demande</p></td></tr>}
            </tbody>
          </table>
        </div>
        {!loading && (
          <div className="px-4 pb-2">
            <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setCurrentPage} rangeStart={rangeStart} rangeEnd={rangeEnd} totalItems={totalItems} />
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl">Détails de la demande</DialogTitle></DialogHeader>
          {selectedQuote && (
            <div className="space-y-6 mt-4">
              <div className="bg-gray-50 border border-gray-200 p-4">
                <h4 className="text-gray-400 text-xs uppercase mb-3 flex items-center gap-2"><User size={14} />Client</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Nom</span><p className="text-gray-900 font-medium">{selectedQuote.quote.first_name} {selectedQuote.quote.last_name}</p></div>
                  <div><span className="text-gray-400">Ville</span><p className="text-gray-900">{selectedQuote.quote.city || '-'}</p></div>
                  <div><span className="text-gray-400">Email</span><p className="text-gray-900">{selectedQuote.quote.email}</p></div>
                  <div><span className="text-gray-400">Téléphone</span><p className="text-gray-900">{selectedQuote.quote.phone}</p></div>
                </div>
                {selectedQuote.quote.message && <div className="mt-4 pt-4 border-t border-gray-200"><span className="text-gray-400 text-xs">Message</span><p className="text-gray-900 text-sm mt-1">{selectedQuote.quote.message}</p></div>}
              </div>
              {selectedQuote.analysis && <div className="bg-gray-50 border border-gray-200 p-4"><h4 className="text-gray-400 text-xs uppercase mb-3">Véhicule</h4><p className="text-gray-900 font-medium">{selectedQuote.analysis.brand || 'Véhicule'} {selectedQuote.analysis.model || ''} {selectedQuote.analysis.year || ''}</p></div>}
              <div><h4 className="text-gray-400 text-xs uppercase mb-3">Pièces ({selectedQuote.quote.selected_parts?.length || 0})</h4><div className="space-y-2">{selectedQuote.quote.selected_parts?.map((p, i) => <div key={i} className="bg-gray-50 border border-gray-200 p-3"><p className="text-gray-900">{p}</p></div>)}</div></div>
              <div className="bg-gray-50 border border-gray-200 p-4"><h4 className="text-gray-400 text-xs uppercase mb-3">Changer le statut</h4><div className="flex flex-wrap gap-2">{statusOptions.map(opt => (<button key={opt.value} onClick={() => updateStatus(selectedQuote.quote.id, opt.value)} disabled={updating || selectedQuote.quote.status === opt.value} className={`px-4 py-2 text-xs font-bold uppercase border transition-colors ${selectedQuote.quote.status === opt.value ? opt.color : 'border-gray-300 text-gray-500 hover:border-gray-400'} disabled:opacity-50`}>{opt.label}</button>))}</div></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminQuotes;
